import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { foldGutter } from '@codemirror/language';
import {
  Annotation,
  Compartment,
  EditorState,
  type Extension,
  Transaction,
} from '@codemirror/state';
import {
  EditorView,
  highlightActiveLine as highlightActiveLineExt,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  placeholder as placeholderExt,
} from '@codemirror/view';
import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import {
  type ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useStableCallback } from '../hooks/useStableCallback';
import { useStableValue } from '../hooks/useStableValue';
import { codeEditorLanguages } from './CodeEditor.languages';
import { codeEditorVariants } from './CodeEditor.styles';
import { codeEditorThemeExtensions } from './CodeEditor.theme';
import type { CodeEditorProps, CodeEditorRef } from './CodeEditor.types';

/**
 * Annotation marking a transaction as a programmatic value sync from the
 * controlled-component pattern (parent setState → useEffect → dispatch).
 * The updateListener uses this to suppress onChange for these transactions,
 * preventing a render loop. Transactions also carry addToHistory.of(false)
 * so programmatic syncs do not pollute the user's undo stack — see spec §5.3.
 */
const External = Annotation.define<boolean>();

function buildEditabilityExt(
  disabled?: boolean,
  readOnly?: boolean,
): Extension {
  // disabled implies readOnly (non-editable AND non-focusable).
  // readOnly only blocks edits; focus stays available — matches <textarea readOnly>.
  if (disabled) {
    return [EditorState.readOnly.of(true), EditorView.editable.of(false)];
  }
  if (readOnly) {
    return EditorState.readOnly.of(true);
  }
  return [];
}

function buildPlaceholderExt(text: string | undefined): Extension {
  return text ? placeholderExt(text) : [];
}

function buildAriaExt(
  labelledBy: string | undefined,
  describedBy: string | undefined,
): Extension {
  const attrs: Record<string, string> = {};
  if (labelledBy) {
    attrs['aria-labelledby'] = labelledBy;
  }
  if (describedBy) {
    attrs['aria-describedby'] = describedBy;
  }
  return Object.keys(attrs).length > 0
    ? EditorView.contentAttributes.of(attrs)
    : [];
}

function buildLineWrapExt(enabled: boolean): Extension {
  return enabled ? EditorView.lineWrapping : [];
}

function buildLineNumbersExt(enabled: boolean): Extension {
  return enabled ? lineNumbers() : [];
}

function buildFoldGutterExt(enabled: boolean): Extension {
  // Override the default markers (`⌄` / `›`) which render with mismatched
  // baselines in most fonts, leaving the open marker visually off-centre next
  // to the closed one. The `▾` / `▸` pair has matching metrics.
  return enabled ? foldGutter({ openText: '▾', closedText: '▸' }) : [];
}

function buildActiveLineExt(enabled: boolean): Extension {
  // highlightActiveLineGutter() also covers the line-number gutter row when
  // showLineNumbers is on; harmless when there is no gutter.
  return enabled ? [highlightActiveLineExt(), highlightActiveLineGutter()] : [];
}

const BareCodeEditor = (
  props: CodeEditorProps,
  ref: ForwardedRef<CodeEditorRef>,
) => {
  const {
    value,
    size = 'default',
    minHeight = '8rem',
    maxHeight = '24rem',
    hasError = false,
    disabled = false,
    className,
    dataTestId,
    id,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const languageCompartment = useStableValue(() => new Compartment());
  const editabilityCompartment = useStableValue(() => new Compartment());
  const placeholderCompartment = useStableValue(() => new Compartment());
  const ariaCompartment = useStableValue(() => new Compartment());
  const lineWrapCompartment = useStableValue(() => new Compartment());
  const lineNumbersCompartment = useStableValue(() => new Compartment());
  const foldGutterCompartment = useStableValue(() => new Compartment());
  const activeLineCompartment = useStableValue(() => new Compartment());

  // Prop-driven effects skip while this is true so the initial mount only
  // configures compartments via EditorState.create — no redundant reconfigure
  // dispatch on top of identical values. Flipped to false by the trailing
  // effect so subsequent renders apply prop changes normally.
  const isFirstRenderRef = useRef(true);
  // Mirrors the live doc text (LF-normalised, as CodeMirror stores it) so the
  // value-sync effect can compare against `value` without re-serialising the
  // full document. Seeded from `view.state.doc.toString()` after mount — using
  // the raw `value` prop here would be off-by-N for CRLF input, since
  // EditorState.create normalises line endings to LF.
  const currentDocRef = useRef('');

  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  const onFocus = useStableCallback(() => {
    props.onFocus?.();
  });
  const onBlur = useStableCallback(() => {
    props.onBlur?.();
  });
  const onKeyDown = useStableCallback((event: KeyboardEvent) => {
    props.onKeyDown?.(event);
  });

  // Build the base extension list once. It captures stable callbacks via refs,
  // not props — so the list never needs to be rebuilt.
  const baseExtensions = useStableValue(() => [
    history(),
    keymap.of([...defaultKeymap, ...historyKeymap]),
    codeEditorThemeExtensions,
    languageCompartment.of(codeEditorLanguages[props.language]()),
    editabilityCompartment.of(
      buildEditabilityExt(props.disabled, props.readOnly),
    ),
    placeholderCompartment.of(buildPlaceholderExt(props.placeholder)),
    ariaCompartment.of(
      buildAriaExt(props['aria-labelledby'], props['aria-describedby']),
    ),
    lineWrapCompartment.of(buildLineWrapExt(props.lineWrapping ?? true)),
    lineNumbersCompartment.of(
      buildLineNumbersExt(props.showLineNumbers ?? false),
    ),
    foldGutterCompartment.of(buildFoldGutterExt(props.showFoldGutter ?? false)),
    activeLineCompartment.of(
      buildActiveLineExt(props.highlightActiveLine ?? false),
    ),
    EditorView.updateListener.of((update) => {
      if (!update.docChanged) {
        return;
      }
      const isExternal = update.transactions.some((tr) =>
        tr.annotation(External),
      );
      if (isExternal) {
        return;
      }
      const newDoc = update.state.doc.toString();
      currentDocRef.current = newDoc;
      onChangeRef.current?.(newDoc);
    }),
  ]);

  // Mount the EditorView once. View persists for the component lifetime.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once on purpose
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const state = EditorState.create({
      doc: value,
      extensions: baseExtensions,
    });
    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;
    // Seed the cache with the LF-normalised doc text so the value-sync effect
    // does not see a stale `currentDocRef.current` derived from a CRLF input.
    currentDocRef.current = view.state.doc.toString();
    view.contentDOM.addEventListener('focus', onFocus);
    view.contentDOM.addEventListener('blur', onBlur);
    view.contentDOM.addEventListener('keydown', onKeyDown);
    return () => {
      view.contentDOM.removeEventListener('focus', onFocus);
      view.contentDOM.removeEventListener('blur', onBlur);
      view.contentDOM.removeEventListener('keydown', onKeyDown);
      view.destroy();
      viewRef.current = null;
      // Reset so a strict-mode remount (or any unmount→remount) starts fresh
      // and the prop-driven effects skip on the next initial commit.
      isFirstRenderRef.current = true;
    };
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }
    const setInputRef = mergeRefs(props.inputRef);
    setInputRef(view.contentDOM);
    return () => {
      setInputRef(null);
    };
  }, [props.inputRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: languageCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: languageCompartment.reconfigure(
        codeEditorLanguages[props.language](),
      ),
    });
  }, [props.language, languageCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: editabilityCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: editabilityCompartment.reconfigure(
        buildEditabilityExt(props.disabled, props.readOnly),
      ),
    });
  }, [props.disabled, props.readOnly, editabilityCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: placeholderCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: placeholderCompartment.reconfigure(
        buildPlaceholderExt(props.placeholder),
      ),
    });
  }, [props.placeholder, placeholderCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ariaCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: ariaCompartment.reconfigure(
        buildAriaExt(props['aria-labelledby'], props['aria-describedby']),
      ),
    });
  }, [props['aria-labelledby'], props['aria-describedby'], ariaCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: lineWrapCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: lineWrapCompartment.reconfigure(
        buildLineWrapExt(props.lineWrapping ?? true),
      ),
    });
  }, [props.lineWrapping, lineWrapCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: lineNumbersCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: lineNumbersCompartment.reconfigure(
        buildLineNumbersExt(props.showLineNumbers ?? false),
      ),
    });
  }, [props.showLineNumbers, lineNumbersCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: foldGutterCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: foldGutterCompartment.reconfigure(
        buildFoldGutterExt(props.showFoldGutter ?? false),
      ),
    });
  }, [props.showFoldGutter, foldGutterCompartment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeLineCompartment is stable (useStableValue)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch({
      effects: activeLineCompartment.reconfigure(
        buildActiveLineExt(props.highlightActiveLine ?? false),
      ),
    });
  }, [props.highlightActiveLine, activeLineCompartment]);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    // Idempotent — same value as before means no dispatch, no cursor reset, no flicker.
    if (currentDocRef.current === value) {
      return;
    }
    view.dispatch({
      // `view.state.doc.length` rather than `value.length` — CodeMirror stores
      // a LF-normalised doc so a CRLF `value` would otherwise overshoot the
      // real document range and throw RangeError out of ChangeSet.of.
      changes: { from: 0, to: view.state.doc.length, insert: value },
      annotations: [External.of(true), Transaction.addToHistory.of(false)],
    });
    // Re-sync the cache from the now-updated, normalised doc so the next
    // identical `value` short-circuits cleanly.
    currentDocRef.current = view.state.doc.toString();
  }, [value]);

  // Trailing effect: must be declared after every prop-driven effect above so
  // those see `isFirstRenderRef.current === true` on the initial commit and
  // skip their redundant reconfigure dispatches.
  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => viewRef.current?.focus(),
      blur: () => viewRef.current?.contentDOM.blur(),
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      id={id}
      data-testid={dataTestId}
      className={codeEditorVariants({ size, hasError, disabled, className })}
      style={{
        // Flex column so the .cm-editor child can flex-fill the wrapper height,
        // which lets CodeMirror's .cm-scroller take over internal scrolling
        // when content exceeds maxHeight (otherwise content would be clipped
        // by overflow-hidden with no way to reach it).
        display: 'flex',
        flexDirection: 'column',
        minHeight,
        maxHeight: maxHeight === 'none' ? undefined : maxHeight,
      }}
    />
  );
};

export const CodeEditor = forwardRef(BareCodeEditor);
